import { useEffect, useState, useSyncExternalStore } from 'react';
import type { Action } from '../../engine';
import { t, type MessageKey } from '../../i18n';
import type { AiClient } from '../../app/aiClient';
import type { Settings } from '../../app/settings';
import { TutorialSession } from '../../app/tutorialSession';
import { CoachPanel } from '../components/CoachPanel';
import { MatchScreen } from './MatchScreen';

const TIPS: readonly MessageKey[] = [
  'tutorial.finish.tip1',
  'tutorial.finish.tip2',
  'tutorial.finish.tip3',
  'tutorial.finish.tip4',
  'tutorial.finish.tip5',
  'tutorial.finish.tip6',
  'tutorial.finish.tip7',
];

/** The closing page: what good players keep in mind, and the way into a first real game. */
function TutorialFinish({
  hasSave,
  onPlayDuel,
  onRules,
  onStartOver,
  onExit,
}: {
  hasSave: boolean;
  onPlayDuel: () => void;
  onRules: () => void;
  onStartOver: () => void;
  onExit: () => void;
}) {
  return (
    <main className="page page-tutorial">
      <header className="page-head">
        <button type="button" className="btn btn-small" onClick={onExit}>
          ← {t('menu.back')}
        </button>
        <h1>{t('tutorial.finish.title')}</h1>
      </header>
      <section className="panel tutorial-tips">
        <p>{t('tutorial.finish.intro')}</p>
        <ol>
          {TIPS.map((tip) => (
            <li key={tip}>{t(tip)}</li>
          ))}
        </ol>
      </section>
      {hasSave && <p className="dim">{t('setup.replaceSave')}</p>}
      <div className="form-actions">
        <button type="button" className="btn btn-primary btn-large" autoFocus onClick={onPlayDuel}>
          {t('tutorial.finish.play')}
          <span className="btn-sub">{t('tutorial.finish.playHint')}</span>
        </button>
        <button type="button" className="btn" onClick={onRules}>
          {t('tutorial.finish.rules')}
        </button>
        <button type="button" className="btn" onClick={onStartOver}>
          {t('tutorial.finish.again')}
        </button>
      </div>
    </main>
  );
}

/**
 * The interactive tutorial (src/tutorial/lessons.ts): each lesson is a position on the real
 * match screen with the coach beside the board, and ends on a page of strategy tips that leads
 * into a first duel.
 */
export function TutorialScreen({
  ai,
  delay,
  settings,
  reduceMotion,
  hasSave,
  onExit,
  onPlayDuel,
  onRules,
}: {
  ai: AiClient;
  delay: (action?: Action) => number;
  settings: Settings;
  reduceMotion: boolean;
  hasSave: boolean;
  onExit: () => void;
  onPlayDuel: () => void;
  onRules: () => void;
}) {
  const [session] = useState(
    () =>
      new TutorialSession({
        ai,
        delay,
        // The lesson's verdict comes once its move has landed: a drawn card in the hand, a card
        // on the pile (the card flights take about 0.6 s). No wait when motion is reduced (the
        // app marks the page for that).
        outcomeDelay: (events) =>
          document.documentElement.classList.contains('reduce-motion')
            ? 0
            : events.some((e) => e.type === 'CardDrawn')
              ? 900
              : 650,
        names: { you: t('match.you'), north: t('tutorial.north') },
      }),
  );
  const snap = useSyncExternalStore(session.subscribe, session.getSnapshot);
  // The lesson's host lives while the screen is shown.
  useEffect(() => {
    session.open();
    return () => session.close();
  }, [session]);

  const { lesson, host, status, index } = snap;
  if (!lesson)
    return (
      <TutorialFinish
        hasSave={hasSave}
        onPlayDuel={onPlayDuel}
        onRules={onRules}
        onStartOver={() => session.goTo(0)}
        onExit={onExit}
      />
    );
  if (!host) return null;
  return (
    <MatchScreen
      key={snap.attempt}
      host={host}
      settings={settings}
      reduceMotion={reduceMotion}
      exitLabel={t('menu.mainMenu')}
      onExit={onExit}
      title={t('tutorial.title')}
      canConcede={false}
      // A lesson that ends the round by mistake restarts; only a round won on purpose is summed up.
      showRoundEnd={status.kind !== 'retry'}
      locked={status.kind !== 'playing'}
      lockedPrompt={
        status.kind === 'retry'
          ? t('tutorial.lockedRetry')
          : status.kind === 'done'
            ? t('tutorial.lockedDone')
            : undefined
      }
      coach={
        <CoachPanel
          key={snap.attempt}
          index={index}
          lesson={lesson}
          status={status}
          solved={snap.progress.solved}
          onNext={() => session.next()}
          onRestart={() => session.restart()}
          onGoTo={(i) => session.goTo(i)}
          onLeave={onExit}
        />
      }
    />
  );
}
